// eslint-disable-next-line no-use-before-define
const QGIV = QGIV || {};

// -------------------------------------------------------------------------
// SECTION: Utility logic that can be used to poll, resize or get the card
// token from the payment iframe.
// -------------------------------------------------------------------------
const paymentIframeHandshakeEvent = "paymentIframeHandshake";
const paymentIframeResizeEvent = "paymentIframeResize";
const paymentIframeSubmissionEvent = "paymentIframeSubmission";
const pollingInterval = 300;
const secondsToPoll = 60;
const urlOfPaymentIframe = `${QGIV.QGIV_HOST}/api/v1/payment/paymentInitialState?formId=${QGIV.Form.id}`;

// Iframes have a content window and we need to access it in order to sent
// a post message to the payment iframe
// https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
/** @type {HTMLIFrameElement | null} */
const $paymentIframe = document.querySelector("#payment-iframe");
const contentWindow = $paymentIframe?.contentWindow;
/** @type {HTMLElement | null} */
const $paymentIframeLoadingAnimation = document.querySelector(
    ".payment-iframe-loading-animation",
);
/** @type {HTMLElement | null} */
const $paymentIframeErrorMessage = document.querySelector(
    ".payment-iframe-error-message",
);

/**
 *
 * @description A utility function that can be used whenever we need to extract
 * data from a post message.
 * @param {MessageEvent} message - The message event object
 * @returns {object} parsedDataFromPostMessage
 */
const getParsedDataFromPostMessage = (message) => {
    const { data = "" } = message;
    let parsedDataFromPostMessage = {};
    let threwErrorWhenParsingJSON = false;

    const hasThePotentialToBeValidJSON =
        typeof data === "string" && data !== "recaptcha-setup";

    if (hasThePotentialToBeValidJSON) {
        try {
            parsedDataFromPostMessage = JSON.parse(data);
        } catch {
            threwErrorWhenParsingJSON = true;
        }

        const isObjectWithKeys =
            typeof parsedDataFromPostMessage === "object" &&
            Object.keys(parsedDataFromPostMessage).length > 0;
        const isParsedDataValidJSON =
            !threwErrorWhenParsingJSON && isObjectWithKeys;

        if (isParsedDataValidJSON) {
            return parsedDataFromPostMessage;
        }
    }

    return {};
};

// -------------------------------------------------------------------------
// SECTION: Poll the payment iframe for a handshake.
// -------------------------------------------------------------------------
let hasReceivedHandshake = false;
let paymentIframePoll;

/**
 *
 * @description Utility function that makes it easy for us to change how long
 * we poll the payment iframe for a handshake. Does this by allowing you to
 * pass in a number of seconds instead of requiring you to manually calculate
 * the number of attempts.
 * @returns {number} - The maximum number of handshake attempts
 */
const getMaxNumberOfHandshakeAttempts = () => {
    const millisecondsToPoll = secondsToPoll * 1000;
    const maxNumberOfHandshakeAttempts = Math.floor(
        millisecondsToPoll / pollingInterval,
    );

    return maxNumberOfHandshakeAttempts;
};

/**
 * Hides the loading animation that is displayed while we poll the payment
 * iframe for a handshake.
 * @returns {void}
 */
const hideLoadingAnimation = () => {
    if ($paymentIframeLoadingAnimation) {
        $paymentIframeLoadingAnimation.classList.add("hide-content");
    }
};

/**
 * Shows the error message that is displayed if our attempt to poll the
 * payment iframe was unsuccessful.
 * @returns {void}
 */
const showErrorMessage = () => {
    if ($paymentIframeErrorMessage) {
        $paymentIframeErrorMessage.classList.remove("hide-content");
    }
};

/**
 * Shows the payment iframe if our attempt to poll the payment iframe was
 * successful. Does not modify the visibility property like the React app as
 * in order to keep things simple in the legacy implementation of this
 * functionality.
 * @returns {void}
 */
const showPaymentIframe = () => {
    if ($paymentIframe) {
        $paymentIframe.classList.remove("hide-content");
    }
};

/**
 *
 * @description Sends post message with the initial handshake whose response
 * will determine whether or not we can effectively communicate with the
 * payment iframe.
 * @returns {void}
 */
const sendHandshakeToPaymentIframe = () => {
    const elementToSendPostMessageTo = contentWindow;

    if ($paymentIframe && !!elementToSendPostMessageTo) {
        const event = paymentIframeHandshakeEvent;
        const message = {
            event,
        };
        const targetOrigin = urlOfPaymentIframe;

        elementToSendPostMessageTo.postMessage(
            JSON.stringify(message),
            targetOrigin,
        );
    }
};

/**
 * @description Stop polling the payment iframe for a handshake by canceling
 * the setInterval call that was used to set it up.
 * @returns {void}
 */
const stopPollingPaymentIframeForHandshake = () => {
    if (!hasReceivedHandshake) {
        hasReceivedHandshake = true;
        clearInterval(paymentIframePoll.intervalId);
    }
};

/**
 * @description Start polling the payment iframe with a special handshake event
 * whose response we await in order to confirm that we are able to effectively
 * use post messages to communicate with the payment iframe.
 * @returns {void}
 */
const startPollingPaymentIframeForHandshake = () => {
    const maxNumberOfHandshakeAttempts = getMaxNumberOfHandshakeAttempts();
    paymentIframePoll = {
        hasStarted: true,
        intervalId: 0,
        numberOfAttempts: 0,
    };

    paymentIframePoll.intervalId = setInterval(() => {
        if (paymentIframePoll.numberOfAttempts < maxNumberOfHandshakeAttempts) {
            paymentIframePoll.numberOfAttempts++;
            sendHandshakeToPaymentIframe();
        } else {
            stopPollingPaymentIframeForHandshake();
            hideLoadingAnimation();
            showErrorMessage();
        }
    }, pollingInterval);

    sendHandshakeToPaymentIframe();
};

/**
 * @description Stops the poll, removes the loading animation and displays the
 * content of the payment iframe whenever we get the handshake response that we
 * have been waiting for.
 * @returns {void}
 */
const handleHandshakeMessage = () => {
    stopPollingPaymentIframeForHandshake();
    hideLoadingAnimation();
    showPaymentIframe();
};

// -------------------------------------------------------------------------
// SECTION: Resize the payment iframe
// -------------------------------------------------------------------------
/**
 * @description The id of the the containing div is used as any attempt to
 * target the iframe directly results in 3px of additional white space being
 * added in between these two elements.
 * @param {object} parsedData - The parsed data from the post message
 * @returns {void}
 */
const handleResizeMessage = (parsedData) => {
    const { data } = parsedData;
    const { height } = data;

    const paymentIframe = document.getElementById("payment-iframe-container");
    const heightInPixels = `${height}px`;

    if (paymentIframe) {
        paymentIframe.style.height = heightInPixels;
    }
};

// -------------------------------------------------------------------------
// SECTION: Get card token from the payment iframe.
// -------------------------------------------------------------------------
let creditCardIframeErrors = [];

/**
 * @description Send a post message to the payment iframe requesting a card token
 * @returns {void}
 */
const sendRequestForCardTokenMessage = () => {
    const elementToSendPostMessageTo = contentWindow;

    if ($paymentIframe && !!elementToSendPostMessageTo) {
        const event = paymentIframeSubmissionEvent;
        const message = {
            event,
        };
        const targetOrigin = urlOfPaymentIframe;

        elementToSendPostMessageTo.postMessage(
            JSON.stringify(message),
            targetOrigin,
        );
    }
};

/**
 *
 * @description In order to successfully send this request we need to:
 * 1. Prepare data to send to the API endpoint.
 * 2. Send a post message that requests a CC submission token from the payment iframe.
 * 3. Add an event listener that monitors whether we have gotten a response to
 * our request for a CC submission token. The handler needs to then process the
 * data in the response and remove the event handler once the promise is fulfilled.
 * @returns {Promise<object>} requestForCardToken
 */
const sendRequestForCardToken = () => {
    const requestForCardToken = new Promise((resolve, reject) => {
        let handleIncomingPostMessage;

        const handleCardTokenMessageResponse = (parsedMessage) => {
            const { data } = parsedMessage;

            if (data.success === true) {
                resolve(parsedMessage);
            } else {
                reject(parsedMessage);
            }
            window.removeEventListener("message", handleIncomingPostMessage);
        };

        handleIncomingPostMessage = (message) => {
            const parsedMessage = getParsedDataFromPostMessage(message);
            if (parsedMessage.event === paymentIframeSubmissionEvent) {
                handleCardTokenMessageResponse(parsedMessage);
            }
        };

        sendRequestForCardTokenMessage();
        window.addEventListener("message", handleIncomingPostMessage);
    });

    return requestForCardToken;
};

/**
 * @description Retrieves the data that enables form submission.
 * @returns {object} An object containing validation errors, success status, and token.
 */
const getDataThatEnablesSubmission = () => {
    const data = {
        validationErrors: {},
        success: true,
        token: "",
    };

    return data;
};

/**
 * @description Extracts the data from the response object.
 * @param {object} response - The response object.
 * @returns {object} The extracted data from the response.
 */
const getDataFromResponse = (response) => {
    const { data } = response;

    return data;
};

/**
 * @description Retrieves the appropriate card token value that we will need
 * @param {boolean} requiresCardTokenFromPaymentIframe - Flag indicating if a card token is required from the payment iframe.
 * @returns {Promise<object>} A promise that resolves with the card token data or rejects with validation errors.
 */
const getCardToken = (requiresCardTokenFromPaymentIframe) => {
    const cardToken = new Promise((resolve, reject) => {
        const dataThatEnablesSubmission = getDataThatEnablesSubmission();

        const handleSuccess = (response) => {
            const data = getDataFromResponse(response);

            resolve(data);
        };

        const handleError = (response) => {
            const data = getDataFromResponse(response);
            reject(data);
        };

        if (!requiresCardTokenFromPaymentIframe) {
            resolve(dataThatEnablesSubmission);
        } else {
            sendRequestForCardToken().then(handleSuccess).catch(handleError);
        }
    });

    return cardToken;
};

/**
 * @description Checks for errors in the store form and updates the creditCardIframeErrors array.
 * @returns {boolean} True if the form has errors, false otherwise.
 */
const getStoreFormHasErrors = () => {
    const $form = document.getElementById("store-checkout-form");

    const visibleRequiredFields = $form
        ? $form.querySelectorAll(
              ".validateEmpty, .validateZipCode, .validateSelectEmpty, validatePhone, validateEmail",
          )
        : [];

    let formErrorCount = 0;

    if (visibleRequiredFields.length > 0) {
        visibleRequiredFields.forEach((field) => {
            const $field = $(field);
            const isFieldVisible = $field.is(":visible"); // Check if the field is visible

            if (isFieldVisible) {
                // Trigger blur on visible fields to check for errors and allow the FE validation to display
                $field.trigger("blur");
                // Check if the field is required
                const fieldHasError = $field.hasClass("has-error");

                // If the field has an error, add it to the creditCardIframeErrors array
                if (fieldHasError) {
                    creditCardIframeErrors.push(
                        `${$field.attr("data-field")} is required`,
                    );
                    formErrorCount += 1;
                }
            }
        });
    }

    return formErrorCount > 0;
};

/**
 * @description Determines whether the form submission should continue based on validation errors.
 * @returns {Promise<boolean>} A promise that resolves to true if the form should continue to submission, false otherwise.
 */
const getShouldContinueToFormSubmission = () => {
    const storeFormHasErrors = getStoreFormHasErrors();

    const shouldContinueToFormSubmission = new Promise((resolve, reject) => {
        // Do not proceed to form submission if:
        // 1. The cc fields threw a validation error
        // 2. The recurring upgrade modal is going to be displayed.
        const handleSuccess = (response) => {
            const { validationErrors, token } = response;
            const hasValidationErrors =
                Object.keys(validationErrors).length > 0;

            // Block form submission if there are payment iframe validation errors or store form errors
            if (hasValidationErrors || storeFormHasErrors) {
                QGIV.StorePaymentIframe.creditCardIframeErrors = [
                    ...creditCardIframeErrors,
                    ...Object.values(validationErrors),
                ];
                creditCardIframeErrors.length = 0;
                resolve(false);
            } else {
                resolve(true);
            }

            // Create and Attach the action input for submission to the form.
            // This is necessary because the form submission does not reliably add the action
            // when the submission is triggered by JS
            const actionInput = document.createElement("input");
            actionInput.classList.add("js-submit-action");
            actionInput.id = "submit-action";
            actionInput.setAttribute("type", "hidden");
            actionInput.setAttribute("name", "action");
            actionInput.setAttribute("value", "Complete Purchase");
            const $paymentIframeContainer = document.getElementById(
                "payment-iframe-container",
            );
            $paymentIframeContainer?.appendChild(actionInput);

            // Attach the token to the form so it can be included in the form submission
            $("#cc-token").val(token);
        };

        // Do not proceed to form submission if:
        // 1. An error was thrown when attempting to get a card token
        const handleError = (response) => {
            const { errors = [] } = response;
            creditCardIframeErrors = errors;
            reject(response);
        };

        // I have intentionally left this hardcoded. If we have made it this far then we will always require a card token.
        // Currently we are handling errors from outside the payment iframe in a different way than we are handling them
        // on the React apps. This is due to the complexities of how the Store Checkout form is being submitted.
        const requiresCardTokenFromPaymentIframe = true;

        getCardToken(requiresCardTokenFromPaymentIframe)
            .then(handleSuccess)
            .catch(handleError);
    });

    return shouldContinueToFormSubmission;
};

QGIV.StorePaymentIframe = {
    contentWindow,
    creditCardIframeErrors,
    getParsedDataFromPostMessage,
    getShouldContinueToFormSubmission,
    handleHandshakeMessage,
    handleResizeMessage,
    paymentIframeHandshakeEvent,
    paymentIframeResizeEvent,
    startPollingPaymentIframeForHandshake,
};
